import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  List,
  ListItem,
  Avatar,
  Typography,
  Badge,
  Box,
  makeStyles,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider
} from '@material-ui/core';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Room as PinIcon,
  VolumeOff as MuteIcon,
  FiberManualRecord as UnreadDotIcon,
  ExpandLess,
  ExpandMore,
  Bookmark as BookmarkIcon
} from '@material-ui/icons';
import { useDate } from '../../hooks/useDate';
import { getBackendUrl } from '../../config';
import { AuthContext } from '../../context/Auth/AuthContext';
import * as chatService from '../../services/chatService';
import toastError from '../../errors/toastError';
import ChatSearchFilter from '../ChatSearchFilter';
import ChatQuickActions from '../ChatQuickActions';
import ChatSortMenu from '../ChatSortMenu';

const useStyles = makeStyles((theme) => ({
  chatList: {
    padding: 0,
    backgroundColor: theme.palette.background.default,
    height: '100%',
    overflow: 'auto',
    ...theme.scrollbarStyles,
  },
  chatItem: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover $menuButton': {
      opacity: 1,
    }
  },
  chatItemUnread: {
    backgroundColor: 'rgba(37, 211, 102, 0.05)',
    borderLeft: `3px solid #25D366`,
    '&:hover': {
      backgroundColor: 'rgba(37, 211, 102, 0.1)',
    }
  },
  activeChatItem: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    }
  },
  chatContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  avatar: {
    width: 48,
    height: 48,
    fontSize: '18px',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    marginRight: theme.spacing(1.5),
  },
  chatInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },  chatHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
    paddingRight: 90,
  },
  chatTitle: {
    fontWeight: 500,
    fontSize: '16px',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    marginRight: 8,
  },  chatTime: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    minWidth: 'fit-content',
    whiteSpace: 'nowrap',
    marginRight: 4,
  },
  topRightContainer: {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    zIndex: 10,
  },
  lastMessage: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: 4,
    lineHeight: 1.3,
  },
  lastMessageUnread: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  chatFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  unreadBadge: {
    backgroundColor: '#25D366',
    color: 'white',
    fontSize: '11px',
    fontWeight: 700,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    border: '2px solid white',
    boxShadow: '0 2px 4px rgba(37, 211, 102, 0.3)',
    animation: '$pulse 2s infinite',
    zIndex: 1,
  },
  '@keyframes pulse': {
    '0%': { 
      opacity: 1,
      transform: 'scale(1)',
      boxShadow: '0 2px 4px rgba(37, 211, 102, 0.3)',
    },
    '50%': { 
      opacity: 0.8,
      transform: 'scale(1.05)',
      boxShadow: '0 4px 8px rgba(37, 211, 102, 0.5)',
    },
    '100%': { 
      opacity: 1,
      transform: 'scale(1)',
      boxShadow: '0 2px 4px rgba(37, 211, 102, 0.3)',
    },
  },  pinnedBadge: {
    color: '#ff9800',
    fontSize: '18px',
  },
  mutedBadge: {
    color: theme.palette.text.disabled,
    fontSize: '18px',
  },  chatBadges: {
    position: 'absolute',
    bottom: 12,
    right: 16,
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },  menuButton: {
    opacity: 0,
    transition: 'opacity 0.2s',
    padding: 4,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.08)',
      opacity: 1,
    }
  },
  typingIndicator: {
    color: theme.palette.primary.main,
    fontSize: '13px',
    fontStyle: 'italic',
    animation: '$typingPulse 1.5s infinite',
  },
  '@keyframes typingPulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  sectionHeader: {
    padding: '12px 16px 8px',
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  archivedCount: {
    fontSize: '12px',
    color: theme.palette.text.disabled,
    marginLeft: 8,
  }
}));

const ModernChatList = ({ 
  chats = [], 
  selectedChatId, 
  typingUsers = new Set(), // ✅ Adicionar prop typingUsers
  onChatSelect, 
  onChatEdit, 
  onChatDelete,
  onChatArchive,
  onChatPin,
  onChatMute,
  onUpdateChat, // ✅ Adicionar nova prop para atualização otimista
  onNewChatClick, // ✅ Nova prop para criar novo chat
  loading = false 
}) => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();
  const { user } = useContext(AuthContext);
  const backendUrl = getBackendUrl() || (process && process.env && process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8080';
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMenuChat, setSelectedMenuChat] = useState(null);
  const [showArchivedChats, setShowArchivedChats] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // ✅ Otimização: Memoiza a função de callback para evitar recriações desnecessárias
  const handleSortChange = React.useCallback((sort, direction) => {
    setSortBy(sort);
    setSortDirection(direction);
  }, []); // As funções `set` do useState são estáveis e não precisam ser listadas como dependências

  const currentUserId = user?.id;
  // Função para obter o título exibido: usa o título do chat; se privado e sem título, usa o nome do outro participante
  const getDisplayTitle = React.useCallback((chat) => {
    if (!chat) return 'Chat sem título';
    if (chat.title && chat.title.trim()) return chat.title;
    if (chat.type === 'private') {
      const otherUser = chat.users?.find(u => u.userId !== currentUserId)?.user;
      const name = otherUser?.name || otherUser?.email || otherUser?.id;
      if (name) return String(name);
    }
    return 'Chat sem título';
  }, [currentUserId]);
  // ✅ Sistema de busca, filtragem e ordenação avançado
  const filteredAndSearchedChats = useMemo(() => {
    let filtered = [...(chats || [])]; // Adicionada verificação para `chats`

    // 🔍 Aplicar busca por texto
    if (searchTerm && searchTerm.trim()) { // Adicionada verificação para `searchTerm`
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(chat => 
        getDisplayTitle(chat)?.toLowerCase().includes(searchLower) ||
        chat.lastMessage?.toLowerCase().includes(searchLower) ||
        chat.users?.some(u => u.user?.name?.toLowerCase().includes(searchLower))
      );
    }

    // 🎯 Aplicar filtros
    if (activeFilters.length > 0) {
      filtered = filtered.filter(chat => {
        const chatUser = chat.users?.find(u => u.userId === currentUserId);
        
        // ✅ Lógica de filtro alterada para 'every' (E) em vez de 'some' (OU)
        // Agora, um chat deve corresponder a TODOS os filtros ativos.
        return activeFilters.every(filter => {
          switch (filter) {
            case 'pinned':
              return chatUser?.isPinned;
            case 'muted':
              return chatUser?.isMuted;
            case 'archived':
              return chatUser?.isArchived;
            case 'unread':
              return chat.unreadCount > 0;
            default:
              return true; // Retorna true para filtros desconhecidos para não quebrar a lógica 'every'
          }
        });
      });
    }    // 📊 Aplicar ordenação
    filtered.sort((a, b) => {
      const chatUserA = a.users?.find(u => u.userId === currentUserId);
      const chatUserB = b.users?.find(u => u.userId === currentUserId);
      
      // ✅ PRIMEIRA PRIORIDADE: Chats fixados sempre no topo
      if (chatUserA?.isPinned && !chatUserB?.isPinned) return -1;
      if (!chatUserA?.isPinned && chatUserB?.isPinned) return 1;
      
      // ✅ SEGUNDA PRIORIDADE: Excluir arquivados da lista principal
      if (chatUserA?.isArchived && !chatUserB?.isArchived) return 1;
      if (!chatUserA?.isArchived && chatUserB?.isArchived) return -1;
      
      // ✅ TERCEIRA PRIORIDADE: Aplicar ordenação selecionada
      let comparison = 0;
      
      switch (sortBy) {
        case 'title':
          comparison = (getDisplayTitle(a) || '').localeCompare(getDisplayTitle(b) || '');
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
        case 'unreadCount':
          comparison = (a.unreadCount || 0) - (b.unreadCount || 0);
          break;
        case 'participantCount':
          comparison = (a.users?.length || 0) - (b.users?.length || 0);
          break;
        case 'updatedAt':
        default:
          comparison = new Date(a.updatedAt) - new Date(b.updatedAt);
          break;
      }
      
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [chats, searchTerm, activeFilters, currentUserId, sortBy, sortDirection]);

  // ✅ Calcular contadores para filtros
  const chatCounts = useMemo(() => {
    const counts = {
      pinned: 0,
      muted: 0,
      archived: 0,
      unread: 0
    };

    chats.forEach(chat => {
      const chatUser = chat.users?.find(u => u.userId === currentUserId);
      
      if (chatUser?.isPinned) counts.pinned++;
      if (chatUser?.isMuted) counts.muted++;
      if (chatUser?.isArchived) counts.archived++;
      if (chat.unreadCount > 0) counts.unread++;
    });

    return counts;
  }, [chats, currentUserId]);

  // ✅ Debug para verificar atualizações da lista
  useEffect(() => {
    // console.log(`📋 ModernChatList recebeu ${chats.length} chats`, chats.map(c => ({
    //   id: c.id,
    //   title: c.title,
    //   unreadCount: c.unreadCount,
    //   lastMessage: c.lastMessage,
    //   updatedAt: c.updatedAt
    // })));
  }, [chats]);  // ✅ Organizar chats: apenas separar arquivados dos demais
  // Os fixados ficam no topo da lista principal junto com os normais
  const { mainChats, archivedChats } = useMemo(() => {
    const main = [];
    const archived = [];

    if (Array.isArray(filteredAndSearchedChats)) {
        filteredAndSearchedChats.forEach(chat => {
            const chatUser = chat.users?.find(u => u.userId === currentUserId);
            
            if (chatUser?.isArchived) {
                archived.push(chat);
            } else {
                main.push(chat);
            }
        });
    }

    return { mainChats: main, archivedChats: archived };
  }, [filteredAndSearchedChats, currentUserId]);

  const handleMenuClick = (event, chat) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedMenuChat(chat);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedMenuChat(null);
  };

  const handleMenuAction = async (action) => {
    if (!selectedMenuChat) return;
    
    try {
      switch (action) {
        case 'edit':
          onChatEdit && onChatEdit(selectedMenuChat);
          break;
          
        case 'delete':
          onChatDelete && onChatDelete(selectedMenuChat);
          break;
          
        case 'pin':
          await handlePinChat(selectedMenuChat);
          break;
          
        case 'mute':
          await handleMuteChat(selectedMenuChat);
          break;
          
        case 'archive':
          await handleArchiveChat(selectedMenuChat);
          break;
          
        default:
          break;
      }
    } catch (error) {
      toastError(error);
      console.error('Erro na ação do menu:', error);
    }
    
    handleMenuClose();
  };

  const handlePinChat = async (chat) => {
    try {
      const chatUser = chat.users?.find(u => u.userId === currentUserId);
      const isPinned = chatUser?.isPinned || false;
      
      // ✅ Atualização Otimista: atualiza a UI imediatamente
      onUpdateChat && onUpdateChat({
        ...chat,
        users: chat.users.map(u => 
          u.userId === currentUserId ? { ...u, isPinned: !isPinned } : u
        )
      });

      await chatService.pinChat(chat.id, !isPinned);
    } catch (error) {
      console.error('Erro ao fixar/desfixar chat:', error);
      toastError(error);
      // 롤백: Reverte a alteração em caso de erro
      onUpdateChat && onUpdateChat(chat);
    }
  };

  const handleMuteChat = async (chat) => {
    try {
      const chatUser = chat.users?.find(u => u.userId === currentUserId);
      const isMuted = chatUser?.isMuted || false;
      const mutedUntil = !isMuted ? new Date(Date.now() + 8 * 60 * 60 * 1000) : null;

      // ✅ Atualização Otimista
      onUpdateChat && onUpdateChat({
        ...chat,
        users: chat.users.map(u => 
          u.userId === currentUserId ? { ...u, isMuted: !isMuted, mutedUntil } : u
        )
      });

      await chatService.muteChat(chat.id, !isMuted, mutedUntil);
    } catch (error) {
      console.error('Erro ao silenciar/dessilenciar chat:', error);
      toastError(error);
      // 롤백: Reverte a alteração em caso de erro
      onUpdateChat && onUpdateChat(chat);
    }
  };

  const handleArchiveChat = async (chat) => {
    try {
      const chatUser = chat.users?.find(u => u.userId === currentUserId);
      const isArchived = chatUser?.isArchived || false;

      // ✅ Atualização Otimista
      onUpdateChat && onUpdateChat({
        ...chat,
        users: chat.users.map(u => 
          u.userId === currentUserId ? { ...u, isArchived: !isArchived } : u
        )
      });

      await chatService.archiveChat(chat.id, !isArchived);
    } catch (error) {
      console.error('Erro ao arquivar/desarquivar chat:', error);
      toastError(error);
      // Reverte a alteração em caso de erro
      onUpdateChat && onUpdateChat(chat);
    }
  };

  const getAvatarText = (title) => {
    if (!title) return '?';
    const words = title.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return title.slice(0, 2).toUpperCase();
  };

  const getUnreadCount = (chat) => {
    // ✅ Método 1: Se tiver unreadCount direto no chat
    if (chat.unreadCount !== undefined) {
      return chat.unreadCount;
    }
    
    // ✅ Método 2: Calcular baseado nas mensagens e último acesso
    if (chat.messages && chat.messages.length > 0) {
      const unreadMessages = chat.messages.filter(msg => 
        msg.senderId !== chat.currentUserId && !msg.readAt
      );
      return unreadMessages.length;
    }
    
    // ✅ Método 3: Se tiver users com unreads
    if (chat.users) {
      const currentUser = chat.users.find(u => u.userId === chat.currentUserId);
      return currentUser?.unreads || 0;
    }
    
    // ✅ Fallback: assumir que tem mensagens não lidas se for a última mensagem não for do usuário atual
    if (chat.lastMessage && chat.lastMessageSenderId && chat.lastMessageSenderId !== chat.currentUserId) {
      return 1; // Pelo menos uma mensagem não lida
    }
    
    return 0;
  };

  // Obter a foto do outro participante em chats privados
  const getAvatarSrc = React.useCallback((chat) => {
    if (!chat || chat.type !== 'private') return null;
    const otherUser = chat.users?.find(u => u.userId !== currentUserId)?.user;
    const profileImage = otherUser?.profileImage;
    const companyId = otherUser?.companyId || user?.companyId;
    if (profileImage && companyId) {
      return `${backendUrl}/public/company${companyId}/user/${profileImage}`;
    }
    return null;
  }, [backendUrl, currentUserId, user?.companyId]);

  const formatLastMessage = (chat) => {
    // ✅ Verificar se há usuários digitando neste chat
    const chatTypingUsers = Array.from(typingUsers).filter(typingUser => 
      typingUser.chatId === chat.id
    );
    
    if (chatTypingUsers.length > 0) {
      const typingNames = chatTypingUsers.map(u => u.userName);
      const typingText = typingNames.length === 1 
        ? `${typingNames[0]} está digitando...`
        : `${typingNames.length} pessoas digitando...`;
      return <span className={classes.typingIndicator}>{typingText}</span>;
    }
    
    if (!chat.lastMessage) {
      return <span style={{ fontStyle: 'italic' }}>Nenhuma mensagem</span>;
    }

    // Trunca mensagem longa
    const maxLength = 45;
    const message = chat.lastMessage.length > maxLength 
      ? chat.lastMessage.substring(0, maxLength) + '...'
      : chat.lastMessage;
      
    const unreadCount = getUnreadCount(chat);
    
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span>{message}</span>
        {unreadCount > 0 && (
          <UnreadDotIcon 
            style={{ 
              color: '#25D366', 
              fontSize: 8,
              marginLeft: 4 
            }} 
          />
        )}
      </span>
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      // Hoje - mostra apenas hora
      return date.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (diffInDays === 1) {
      return 'Ontem';
    } else if (diffInDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    }
  };
  // ✅ Função para renderizar um item de chat
  const renderChatItem = (chat, isPinned = false) => {
    const unreadCount = getUnreadCount(chat);
    const isSelected = selectedChatId === chat.uuid || selectedChatId === chat.id;
    const hasUnread = unreadCount > 0;
    const chatUser = chat.users?.find(u => u.userId === currentUserId);
    
    return (
      <ListItem
        key={chat.id}
        className={`${classes.chatItem} ${
          isSelected ? classes.activeChatItem : ''
        } ${hasUnread ? classes.chatItemUnread : ''}`}
        onClick={() => onChatSelect && onChatSelect(chat)}
        disableGutters
      >
        <div className={classes.chatContent}>
          <Badge
            badgeContent={unreadCount > 99 ? '99+' : unreadCount}
            classes={{ badge: classes.unreadBadge }}
            invisible={unreadCount === 0}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            style={{ position: 'relative' }}
          >
            <Avatar
              className={classes.avatar}
              src={getAvatarSrc(chat) || undefined}
              alt={getDisplayTitle(chat)}
            >
              {!getAvatarSrc(chat) && getAvatarText(getDisplayTitle(chat))}
            </Avatar>
          </Badge>

          <div className={classes.chatInfo}>            <div className={classes.chatHeader}>
              <Typography className={classes.chatTitle}>
                {getDisplayTitle(chat)}
              </Typography>
            </div>

            <Typography 
              className={`${classes.lastMessage} ${unreadCount > 0 ? classes.lastMessageUnread : ''}`}
              component="div"
            >
              {formatLastMessage(chat)}
            </Typography>

            <div className={classes.chatFooter}>
              <div className={classes.badges}>
                {/* Badges movidos para posição absoluta */}
              </div>
            </div>          </div>
          
          {/* ✅ Container com hora e menu no topo direito */}
          <div className={classes.topRightContainer}>
            <Typography className={classes.chatTime}>
              {formatTime(chat.updatedAt)}
            </Typography>
            <ChatQuickActions
              chat={chat}
              currentUserId={currentUserId}
              onEdit={onChatEdit}
              onDelete={onChatDelete}
              onPin={handlePinChat}
              onMute={handleMuteChat}
              onArchive={handleArchiveChat}
            />          </div>
          
          {/* ✅ Ícones de status no canto direito inferior */}
          <div className={classes.chatBadges}>
            {isPinned && (
              <PinIcon 
                className={classes.pinnedBadge} 
                fontSize="small"
              />
            )}
            {chatUser?.isMuted && (
              <MuteIcon className={classes.mutedBadge} fontSize="small" />
            )}
          </div>
        </div>
      </ListItem>
    );
  };

  // Remover este useEffect antigo que não é mais necessário
  // const filteredAndSearchedChats já cuida disso

  if (loading) {
    return (
      <>
        <ChatSearchFilter 
          onSearchChange={setSearchTerm}
          onFilterChange={setActiveFilters}
          searchValue={searchTerm}
          activeFilters={activeFilters}
          chatCounts={chatCounts}
          onSortChange={handleSortChange} // ✅ Usa a função memoizada
          currentSort={sortBy}
          currentSortDirection={sortDirection}
          onNewChatClick={onNewChatClick} // ✅ Adicionar prop que estava faltando
        />
        <div className={classes.emptyState}>
          <Typography>Carregando chats...</Typography>
        </div>
      </>
    );
  }
  if (!filteredAndSearchedChats || filteredAndSearchedChats.length === 0) {
    return (
      <>
        <ChatSearchFilter 
          onSearchChange={setSearchTerm}
          onFilterChange={setActiveFilters}
          searchValue={searchTerm}
          activeFilters={activeFilters}
          chatCounts={chatCounts}
          onSortChange={handleSortChange} // ✅ Usa a função memoizada
          currentSort={sortBy}
          currentSortDirection={sortDirection}
          onNewChatClick={onNewChatClick} // ✅ Adicionar prop que estava faltando
        />
        <div className={classes.emptyState}>
          <Typography>
            {searchTerm || activeFilters.length > 0 
              ? 'Nenhum chat encontrado com os filtros aplicados' 
              : 'Nenhum chat encontrado'
            }
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {searchTerm || activeFilters.length > 0 
              ? 'Tente ajustar sua busca ou filtros'
              : 'Crie um novo chat para começar'
            }
          </Typography>
        </div>
      </>
    );
  }
  return (
    <>
      <ChatSearchFilter 
        onSearchChange={setSearchTerm}
        onFilterChange={setActiveFilters}
        searchValue={searchTerm}
        activeFilters={activeFilters}
        chatCounts={chatCounts}
        onSortChange={handleSortChange} // ✅ Usa a função memoizada
        currentSort={sortBy}
        currentSortDirection={sortDirection}
        onNewChatClick={onNewChatClick} // ✅ Passa a função para criar novo chat
      />      <List className={classes.chatList}>
        {/* ✅ Lista principal: chats fixados no topo + chats normais */}
        {mainChats.map((chat) => {
          const chatUser = chat.users?.find(u => u.userId === currentUserId);
          const isPinned = chatUser?.isPinned || false;
          return renderChatItem(chat, isPinned);
        })}

        {/* ✅ Chats Arquivados */}
        {archivedChats.length > 0 && (
          <>
            <Divider />
            <div 
              className={classes.sectionHeader}
              onClick={() => setShowArchivedChats(!showArchivedChats)}
            >
              <Typography className={classes.sectionTitle}>
                📦 Arquivados
                <span className={classes.archivedCount}>({archivedChats.length})</span>
              </Typography>
              {showArchivedChats ? <ExpandLess /> : <ExpandMore />}
            </div>
            <Collapse in={showArchivedChats}>
              {archivedChats.map((chat) => renderChatItem(chat))}
            </Collapse>
          </>
        )}
      </List>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          style: { minWidth: 160 }
        }}
      >
        {selectedMenuChat?.type === 'group' && (
          <MenuItem onClick={() => handleMenuAction('edit')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Editar" />
          </MenuItem>
        )}
        
        <MenuItem onClick={() => handleMenuAction('pin')}>
          <ListItemIcon>
            <BookmarkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={
            selectedMenuChat?.users?.find(u => u.userId === currentUserId)?.isPinned 
              ? "Desfixar" 
              : "Fixar"
          } />
        </MenuItem>
        
        <MenuItem onClick={() => handleMenuAction('mute')}>
          <ListItemIcon>
            <MuteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={
            selectedMenuChat?.users?.find(u => u.userId === currentUserId)?.isMuted 
              ? "Dessilenciar" 
              : "Silenciar"
          } />
        </MenuItem>
          <MenuItem onClick={() => handleMenuAction('archive')}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={
            selectedMenuChat?.users?.find(u => u.userId === currentUserId)?.isArchived 
              ? "Desarquivar" 
              : "Arquivar"
          } />
        </MenuItem>
        
        <MenuItem onClick={() => handleMenuAction('delete')} style={{ color: '#f44336' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" style={{ color: '#f44336' }} />
          </ListItemIcon>
          <ListItemText primary="Excluir" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ModernChatList;
